/**
 * BackspaceNavGuard — desktop shell only: kill WKWebView's Backspace-goes-
 * back default.
 *
 * The shell's wry layer is a WKWebView, and WebKit still treats Backspace
 * outside an editable element as history back. Task navigation rides
 * history.replaceState (task/layout.tsx), so "back" doesn't step between
 * tasks — it exits the workspace to whatever real entry preceded it,
 * usually /home. Founder repro: click the embedded browser's URL bar (not
 * editable until the wheel is held, so nothing gains focus), press
 * Backspace → the whole app jumps home.
 *
 * The guard prevents the default ONLY for non-editable targets — inputs,
 * textareas, selects, and contenteditable keep native character deletion
 * untouched. Regular browsers dropped backspace-navigation years ago, so
 * the behavior this suppresses exists only in the shell; the component is
 * additionally gated on isDesktopShell and renders nothing.
 */
"use client";

import { useEffect } from "react";
import { isDesktopShell } from "@/lib/desktop-shell";

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT" ||
    target.isContentEditable
  );
}

export function BackspaceNavGuard() {
  useEffect(() => {
    if (!isDesktopShell()) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Backspace") return;
      if (!isEditable(e.target)) e.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return null;
}
